package com.yendo.mx.utils

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import dev.gitlive.firebase.firestore.FieldValue
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

/**
 * 🚀 EXPECT: Función nativa para Google Analytics
 */
expect fun logFirebaseEvent(name: String, params: Map<String, String>)

/**
 * 📊 GESTOR DE ANALÍTICA PUBLICITARIA (YENDO OS)
 * Versión Ultra-Rápida a 120 FPS: Deduplicación en memoria + Despacho Asíncrono en Hilo Secundario.
 */
object AdAnalyticsManager {
    private val anunciosYaVistos = mutableSetOf<String>()
    private val scope = CoroutineScope(Dispatchers.Default)
    
    /**
     * 👀 Registra una Impresión (Vista) - Máxima velocidad sin tirones
     * @param adId Identificador único del anuncio o negocio
     * @param adType Tipo de anuncio (PROMO_FLASH, BANNER_HOME, CARRUSEL)
     * @param context Contexto adicional (ej: nombre de la sección)
     */
    fun logAdView(adId: String, adType: String, context: String = "") {
        if (adId.isBlank()) return
        val claveUnica = "${adId}_${adType}_$context"

        // 🚀 DEDUPLICACIÓN EN MEMORIA (0ms): Un anuncio solo se registra 1 sola vez por sesión
        if (anunciosYaVistos.contains(claveUnica)) return
        anunciosYaVistos.add(claveUnica)

        // 🚀 DESPACHO ASÍNCRONO EN HILO SECUNDARIO (Deja el UI Thread libre para 120 FPS)
        scope.launch {
            try {
                logFirebaseEvent("ad_impression", mapOf(
                    "ad_id" to adId,
                    "ad_type" to adType,
                    "section" to context
                ))
            } catch (e: Exception) { }
        }
    }

    /**
     * 🖱️ Registra un Click (Tap)
     */
    fun logAdClick(adId: String, adType: String, context: String = "") {
        if (adId.isBlank()) return

        try {
            // 🚀 Google Analytics
            logFirebaseEvent("ad_click", mapOf(
                "ad_id" to adId,
                "ad_type" to adType,
                "section" to context
            ))
        } catch (e: Exception) {
            println("YENDO_LOG ANALYTICS: ❌ Error en logAdClick - ${e.message}")
        }
    }

    /**
     * 🛒 Registra una Vista de Producto (view_item)
     * Optimizada con parámetros estándar de Google eCommerce
     */
    fun logItemView(productId: String, productName: String, price: Double, merchantName: String) {
        if (productId.isBlank()) return
        
        try {
            // Usamos nombres de parámetros estándar de Firebase para reportes automáticos de ventas
            logFirebaseEvent("view_item", mapOf(
                "item_id" to productId,
                "item_name" to productName,
                "item_brand" to merchantName,
                "price" to price.toString(),
                "currency" to "MXN",
                "value" to price.toString()
            ))
        } catch (e: Exception) {
            println("YENDO_LOG ANALYTICS: ❌ Error en logItemView - ${e.message}")
        }
    }

    /**
     * 🛍️ Registra cuando el usuario agrega un producto al carrito (add_to_cart)
     */
    fun logAddToCart(productId: String, productName: String, price: Double, quantity: Int, merchantName: String) {
        if (productId.isBlank()) return
        try {
            logFirebaseEvent("add_to_cart", mapOf(
                "item_id" to productId,
                "item_name" to productName,
                "item_brand" to merchantName,
                "price" to price.toString(),
                "quantity" to quantity.toString(),
                "currency" to "MXN",
                "value" to (price * quantity).toString()
            ))
        } catch (e: Exception) {
            println("YENDO_LOG ANALYTICS: ❌ Error en logAddToCart - ${e.message}")
        }
    }

    /**
     * 💳 Registra cuando el usuario realiza un pedido exitosamente (purchase)
     */
    fun logPurchase(orderId: String, totalValue: Double, itemsCount: Int, merchantName: String) {
        if (orderId.isBlank()) return
        try {
            logFirebaseEvent("purchase", mapOf(
                "transaction_id" to orderId,
                "value" to totalValue.toString(),
                "currency" to "MXN",
                "items_count" to itemsCount.toString(),
                "merchant_name" to merchantName
            ))
        } catch (e: Exception) {
            println("YENDO_LOG ANALYTICS: ❌ Error en logPurchase - ${e.message}")
        }
    }
}
